import { supabase } from './supabase'

export interface Interest {
  id: string
  name: string
  slug: string
  created_at?: string
}

export interface UserWithSharedInterests {
  user_id: string
  shared_interest_count: number
  shared_interests: string[]
}

export const interestsService = {
  /**
   * Get all available interests
   */
  async getAllInterests(): Promise<Interest[]> {
    const { data, error } = await supabase
      .from('interests')
      .select('*')
      .order('name')

    if (error) throw error
    return data || []
  },

  /**
   * Get a user's selected interests
   */
  async getUserInterests(userId: string): Promise<Interest[]> {
    const { data, error } = await supabase
      .from('user_interests')
      .select(`
        interest_id,
        interests (
          id,
          name,
          slug
        )
      `)
      .eq('user_id', userId)

    if (error) throw error

    return data?.map(item => item.interests as any).filter(Boolean) || []
  },

  /**
   * Find users who share interests with a target user
   */
  async getUsersWithSharedInterests(userId: string): Promise<UserWithSharedInterests[]> {
    const { data, error } = await supabase
      .rpc('get_users_with_shared_interests', { target_user_id: userId })

    if (error) throw error
    return data || []
  },

  /**
   * Get users who have a specific interest
   */
  async getUsersByInterest(interestId: string) {
    const { data, error } = await supabase
      .from('user_interests')
      .select(`
        user_id,
        users (
          id,
          first_name,
          last_name,
          username,
          avatar_url
        )
      `)
      .eq('interest_id', interestId)

    if (error) throw error
    return data?.map(item => item.users).filter(Boolean) || []
  },

  /**
   * Save user interests (replaces all existing interests)
   */
  async saveUserInterests(userId: string, interestIds: string[]) {
    // Delete all existing interests
    await supabase
      .from('user_interests')
      .delete()
      .eq('user_id', userId)

    // Insert new interests
    if (interestIds.length > 0) {
      const interestsToInsert = interestIds.map(interestId => ({
        user_id: userId,
        interest_id: interestId
      }))

      const { error } = await supabase
        .from('user_interests')
        .insert(interestsToInsert)

      if (error) throw error
    }
  }
}
